import {
  bffCrmMembersCareGaps,
  bffCrmMembersContracts,
  bffCrmMembersOverview,
  bffCrmMembersQueues,
  type BffCrmMembersCareGapsRetrieveResponse,
  type BffCrmMembersContractsRetrieveResponse,
  type BffCrmMembersOverviewRetrieveQueryParams,
  type BffCrmMembersOverviewRetrieveResponse,
  type BffCrmMembersQueuesRetrieveResponse,
} from '@fireflyhealth/api'
import { keepPreviousData } from '@tanstack/react-query'
import { useQuery } from '~/shared/hooks/query'
import { fetchAssigneeGroups } from '~/entities/assignee-group'

export type Product = NonNullable<BffCrmMembersOverviewRetrieveQueryParams['product']>
type MembersOverview = BffCrmMembersOverviewRetrieveResponse
type MembersCareGaps = BffCrmMembersCareGapsRetrieveResponse
type MembersQueue = BffCrmMembersQueuesRetrieveResponse
/** One member on a queue, as the queues endpoint returns it. */
export type MembersQueueRow = MembersQueue['results'][number]
type MembersContracts = BffCrmMembersContractsRetrieveResponse

export const PRODUCT_OPTIONS: ReadonlyArray<{ value: Product; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'care_only', label: 'Care Only' },
  { value: 'health_plan', label: 'Health Plan' },
]

// Population numbers are refreshed by a daily pipeline and cached server-side, so a
// few minutes of client staleness is fine.
const STALE_TIME = 5 * 60 * 1000

// Scope params shared by the dashboard endpoints: product is always sent; contract
// only when a specific one is selected (omitted = all contracts).
const scopeParams = (product: Product, contract?: number | null) => ({
  product,
  ...(contract != null ? { contract } : {}),
})

export const useMembersOverview = (product: Product, contract?: number | null) => {
  const params = scopeParams(product, contract)
  return useQuery(
    bffCrmMembersOverview.retrieveQueryKey(params),
    async (): Promise<MembersOverview> => (await bffCrmMembersOverview.retrieve({ params })).data,
    { staleTime: STALE_TIME },
  )
}

/**
 * Open care gaps per measure, on a separate endpoint from the KPI overview so the
 * slower per-measure rollup loads on its own card without holding up the tiles.
 */
export const useMembersCareGaps = (product: Product, contract?: number | null) => {
  const params = scopeParams(product, contract)
  return useQuery(
    bffCrmMembersCareGaps.retrieveQueryKey(params),
    async (): Promise<MembersCareGaps> => (await bffCrmMembersCareGaps.retrieve({ params })).data,
    { staleTime: STALE_TIME },
  )
}

/** Contract options for the dashboard's contract filter (contracts with members here). */
export const useMembersContracts = () =>
  useQuery(
    ['members-dashboard-contracts'],
    async (): Promise<MembersContracts> => (await bffCrmMembersContracts.retrieve()).data,
    { staleTime: STALE_TIME },
  )

/**
 * Count + a short preview of members in a queue, for the overview's priority-queue
 * cards. The full drill-down lives at /members/queues/<key>.
 */
export const useMembersQueueSummary = (
  key: string,
  product: Product,
  contract?: number | null,
  previewLimit = 3,
) => {
  const params = { ...scopeParams(product, contract), limit: previewLimit }
  return useQuery(
    bffCrmMembersQueues.retrieveQueryKey({ key }, params),
    async (): Promise<MembersQueue> =>
      (await bffCrmMembersQueues.retrieve({ key }, { params })).data,
    { staleTime: STALE_TIME },
  )
}

/**
 * Team-level assignee groups for the drill-down team filter. `groups_only` excludes
 * the auto-created per-member groups, so only real teams appear in the picker; retired
 * (deprecated) groups are dropped too, since there is nothing left to filter or manage on them.
 */
export const useTeamGroups = (enabled = true) =>
  useQuery(
    ['members-dashboard-team-groups'],
    () => fetchAssigneeGroups().then(groups => groups.filter(group => !group.is_deprecated)),
    {
      staleTime: STALE_TIME,
      enabled,
    },
  )

/**
 * Full paginated queue for the drill-down grid (server-side limit/offset).
 *
 * `ownerGroupId` narrows the case-backed queues (overdue / awaiting-reply) to a team
 * and its members; it is ignored server-side by the other queues.
 */
export const useMembersQueue = (
  key: string,
  product: Product,
  page: number,
  pageSize: number,
  ownerGroupId?: number,
  contract?: number | null,
) => {
  const params = {
    ...scopeParams(product, contract),
    limit: pageSize,
    offset: page * pageSize,
    ...(ownerGroupId != null ? { owner_group: ownerGroupId } : {}),
  }
  return useQuery(
    bffCrmMembersQueues.retrieveQueryKey({ key }, params),
    async (): Promise<MembersQueue> =>
      (await bffCrmMembersQueues.retrieve({ key }, { params })).data,
    { staleTime: STALE_TIME, placeholderData: keepPreviousData },
  )
}
